import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

const SCROLL_THRESHOLD = 50;

/**
 * Hook that manages auto-scroll behavior for a scrollable container.
 * Auto-scrolls to bottom during streaming unless the user has manually scrolled up.
 * Exposes a button state and scroll-to-bottom action for resuming auto-scroll.
 */
export function useAutoScroll(
  containerRef: RefObject<HTMLDivElement | null>,
  isStreaming: boolean
): {
  showScrollButton: boolean;
  scrollToBottom: () => void;
} {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const userScrolledUp = useRef(false);

  const isAtBottom = useCallback((el: HTMLElement): boolean => {
    return el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD;
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    userScrolledUp.current = false;
    setShowScrollButton(false);
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [containerRef]);

  // Listen for user scroll events to detect manual scroll-up
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleScroll = () => {
      if (isAtBottom(el)) {
        userScrolledUp.current = false;
        setShowScrollButton(false);
      } else {
        userScrolledUp.current = true;
        setShowScrollButton(true);
      }
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, [containerRef, isAtBottom]);

  // Auto-scroll during streaming unless user has scrolled up
  useEffect(() => {
    if (!isStreaming) return;

    const el = containerRef.current;
    if (!el) return;

    if (!userScrolledUp.current) {
      el.scrollTop = el.scrollHeight;
    }
  });

  return { showScrollButton, scrollToBottom };
}
